import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { SystemHealthSnapshot } from '../types';
import SectionCard from './SectionCard';
import { zoneStatTileClass, zoneStatTileLabelClass, type OpsIntensity } from '../theme';

interface OnlineUsersCardProps {
  onlineUsers: SystemHealthSnapshot['onlineUsers'];
  intensity?: OpsIntensity;
}

const OnlineUsersCard: React.FC<OnlineUsersCardProps> = ({ onlineUsers, intensity = 'admin' }) => {
  const { colors } = useTheme();
  if (!onlineUsers) return null;

  return (
    <SectionCard
      zone="people"
      intensity={intensity}
      title="Online now"
      subtitle="WebSocket connections to this backend (real-time)"
    >
      <div className="flex flex-wrap gap-3 mb-4">
        <div className={zoneStatTileClass('people')}>
          <p className={zoneStatTileLabelClass('people')}>Users connected</p>
          <p className={cn('text-2xl font-bold tabular-nums mt-0.5 text-violet-800 dark:text-violet-200')}>
            {onlineUsers.uniqueUsers}
          </p>
        </div>
        <div className={zoneStatTileClass('people')}>
          <p className={zoneStatTileLabelClass('people')}>Open sockets</p>
          <p className={cn('text-2xl font-bold tabular-nums mt-0.5', colors.text)}>{onlineUsers.totalSockets}</p>
        </div>
      </div>
      {onlineUsers.users.length === 0 ? (
        <p className={cn('text-sm', colors.textMuted)}>No users connected via WebSocket right now.</p>
      ) : (
        <ul className="space-y-0 rounded-lg border border-violet-200/50 dark:border-violet-800/40 overflow-hidden divide-y divide-violet-100 dark:divide-violet-900/40">
          {onlineUsers.users.map((u, i) => (
            <li
              key={`${u.role}-${u.userId}`}
              className={cn(
                'flex items-center justify-between gap-2 text-sm py-2.5 px-3',
                i % 2 === 1 && 'bg-violet-50/40 dark:bg-violet-950/15',
              )}
            >
              <span className={cn('truncate', colors.text)}>
                {u.displayName || u.email}
                <span className={cn('ml-2 text-xs font-medium text-violet-600/80 dark:text-violet-300/80')}>
                  {u.role}
                </span>
              </span>
              <span className={cn('text-xs tabular-nums shrink-0', colors.textMuted)}>
                {u.sessionCount} session{u.sessionCount === 1 ? '' : 's'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
};

export default OnlineUsersCard;
